/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import { Box } from "@mui/material";
import { PermIdentity } from "@material-ui/icons";

import Card from "components/UI/Card/Card";
import CardHeader from "components/UI/Card/CardHeader";
import CardIcon from "components/UI/Card/CardIcon";
import CardBody from "components/UI/Card/CardBody";
import GridContainer from "components/UI/Grid/GridContainer";
import GridItemTS from "components/UI/Grid/GridItemTS";

import {
  DetailsContent,
  Divider,
  Line,
  StyledParagraph,
  StyledParagraphValue,
  StyledTag,
  Title,
} from "./GeneralTab.styles";
import { htmlToText } from "utils/functions";

type BuildDetailsItem = {
  text: string;
  value: string | React.ReactNode | boolean | null | undefined;
  html?: boolean;
};

export type BuildDetailsSection = {
  title: string;
  items: BuildDetailsItem[];
};

type GeneralTabProps = {
  isLoading: boolean;
  buildDetails: BuildDetailsSection[];
};

const GeneralTab: React.FC<GeneralTabProps> = ({ isLoading, buildDetails }) => {
  const { t } = useTranslation("sow-profile");

  function skeletonLoading() {
    return (
      <>
        <Skeleton data-cy="skeleton-loading" />
        <Skeleton data-cy="skeleton-loading" />
        <Skeleton data-cy="skeleton-loading" />
        <Skeleton data-cy="skeleton-loading" />
      </>
    );
  }

  const firstHalf = buildDetails.slice(0, -1);
  const secondHalf = buildDetails.slice(-1);

  return (
    <GridContainer data-cy="general-tab-container">
      <GridItemTS
        key="data"
        xs={12}
        sm={12}
        md={6}
        data-cy="general-tab-section-1"
      >
        <Card>
          <CardHeader color="rose" icon data-cy="general-tab-header-1">
            <CardIcon color="rose">
              <PermIdentity />
            </CardIcon>
            <Title>
              {t("Details", { ns: "profile" })}
              <StyledTag>fieldglass</StyledTag>
            </Title>
          </CardHeader>

          <CardBody>
            <DetailsContent>
              {isLoading ? (
                skeletonLoading()
              ) : (
                <>
                  {firstHalf.map((section, sectionIndex) => (
                    <Box
                      key={sectionIndex}
                      data-cy={`general-tab-section-1-item-${sectionIndex}`}
                    >
                      <Divider>
                        <h4>{section.title}</h4>
                      </Divider>
                      {section.items.map((item, itemIndex) => (
                        <Line
                          key={itemIndex}
                          data-cy={`general-tab-section-1-item-${sectionIndex}-line-${itemIndex}`}
                        >
                          {!item.html && (
                            <StyledParagraph>{item.text}:</StyledParagraph>
                          )}
                          <StyledParagraphValue>
                            {Array.isArray(item.value)
                              ? item.value.join(", ")
                              : item?.html
                              ? htmlToText(String(item.value ?? ""))
                              : item.value}
                          </StyledParagraphValue>
                        </Line>
                      ))}
                    </Box>
                  ))}
                </>
              )}
            </DetailsContent>
          </CardBody>
        </Card>
      </GridItemTS>
      <GridItemTS
        key="data-2"
        xs={12}
        sm={12}
        md={6}
        data-cy="general-tab-section-2"
      >
        <Card>
          <CardBody>
            <DetailsContent>
              {isLoading
                ? skeletonLoading()
                : secondHalf.map((section, sectionIndex) => (
                    <Box
                      key={sectionIndex}
                      data-cy={`general-tab-section-2-item-${sectionIndex}`}
                    >
                      <Divider>
                        <h4>{section.title}</h4>
                      </Divider>
                      {section.items.map((item, itemIndex) => (
                        <Box
                          key={itemIndex}
                          data-cy={`general-tab-section-2-item-${sectionIndex}-box-${itemIndex}`}
                        >
                          <span>{item.value}</span>
                        </Box>
                      ))}
                    </Box>
                  ))}
            </DetailsContent>
          </CardBody>
        </Card>
      </GridItemTS>
    </GridContainer>
  );
};

export default GeneralTab;
